import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { TEMPLATE_KEYS } from "@/lib/template-data";
import { getExploreDict } from "@/lib/i18n/explore";
import { useLocale } from "@/hooks/useLocale";

// Dynamically import all explore images
const TEMPLATE_IMAGES: Record<string, string> = {};
const imageModules = import.meta.glob("@/assets/explore/*.jpg", { eager: true, import: "default" }) as Record<string, string>;
for (const [path, url] of Object.entries(imageModules)) {
  const fileName = path.split("/").pop()?.replace(".jpg", "") || "";
  TEMPLATE_IMAGES[fileName] = url;
}

// Map gameKey to image filename
const KEY_TO_IMAGE: Record<string, string> = {
  cartel: "cartel-wars", shadow: "shadow-cell", galactic: "galactic-conquest",
  godfather: "godfathers-heir", feudal: "feudal-overlord", rebel: "rebel-command",
  dungeon: "dungeon-inc", potion: "potion-empire", tavern: "tavern-keeper",
  station: "space-station", crime: "crime-lord", gladiator: "gladiator-school",
  mercenary: "mercenary-company", hogwarts: "enchanted-academy", westeros: "iron-kingdoms",
  middleearth: "ancient-wilds", starfleet: "stellar-academy", jedi: "exile-of-order",
  peaky: "razor-gang", naruto: "shadow-village", darkest: "darkest-descent",
  wasteland: "wasteland", court: "court-thorns", pirate: "pirate-captain",
  vampire: "vampire", courtroom: "courtroom", island: "survival-island",
  heist: "heist-crew", political: "political-thriller", sky: "sky-citadel",
  underdark: "underdark-colony", clockwork: "clockwork-city", arcane: "arcane-academy",
  sand: "sand-kingdoms", frozen: "frozen-throne", verdant: "verdant-spire",
  deep: "deep-haven", neon: "neon-pit", plague: "plague-ward", iron: "iron-rail",
  silk: "silk-road",
  hw: "horror-wasteland", hh: "horror-haunted-house", hu: "horror-underworld",
  hi: "horror-lost-island", hg: "horror-ghost-ship", ha: "horror-alien-encounter",
  hv: "horror-alien-invasion", has: "horror-asylum",
};

interface TemplateSelectorProps {
  onSelect: (gameKey: string, title: string, description: string) => void;
}

export function TemplateSelector({ onSelect }: TemplateSelectorProps) {
  const { locale, t } = useLocale();
  const et = getExploreDict(locale);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const templates = useMemo(() => {
    return TEMPLATE_KEYS.map(key => ({
      key,
      title: et[`game_${key}_title`] || key,
      desc: et[`game_${key}_desc`] || "",
      image: TEMPLATE_IMAGES[KEY_TO_IMAGE[key] || ""] || "",
    }));
  }, [et]);

  const filtered = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.toLowerCase();
    return templates.filter(t =>
      t.title.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q)
    );
  }, [templates, search]);

  const visible = showAll ? filtered : filtered.slice(0, 6);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          placeholder={t.wb_template_search}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-8 h-8 text-sm"
        />
      </div>

      <ScrollArea className={cn("pr-1", showAll ? "max-h-[350px]" : "")}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {visible.map(t => (
            <button
              key={t.key}
              onClick={() => onSelect(t.key, t.title, t.desc)}
              className="group relative rounded-lg border border-border bg-card overflow-hidden text-left hover:border-primary/50 transition-all hover:shadow-md"
            >
              {t.image && (
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={t.image}
                    alt={t.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    loading="lazy"
                  />
                </div>
              )}
              <div className="p-2 space-y-0.5">
                <div className="text-xs font-medium truncate">{t.title}</div>
                <div className="text-[10px] text-muted-foreground line-clamp-2 leading-tight">{t.desc}</div>
              </div>
              <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Badge className="text-[9px] h-4 px-1.5 bg-primary text-primary-foreground">
                  <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                  Use
                </Badge>
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>

      {filtered.length > 6 && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full gap-1 text-xs h-7"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {showAll ? "Show less" : `Show all (${filtered.length})`}
        </Button>
      )}

      {filtered.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-4">No templates match your search</p>
      )}
    </div>
  );
}
